import Fuse from "fuse.js";
import { createSelector } from "reselect";

import { normalizeLower, normalizePicOrAbn } from "./sideLoadedBusinesses";
import { PIC_DELIMITER } from "constants/sideLoadedBusinesses";
import { BusinessMatchedField } from "constants/temporaryBusinesses";
import { getLivestockSaleId } from "lib/navigation";

import { selectBusinessesIdsInSaleByType } from "./aggrid/businessesInSale";
import {
  getBusinesses,
  getProperties,
  getSideLoadedBusinesses,
} from "./root";

function getBusinessesById(state: State): Record<string, Business> {
  return getBusinesses(state) as unknown as Record<string, Business>;
}

export interface BusinessMatchCandidate {
  business: Business;
  matchedFields: BusinessMatchedField[];
}

interface PropertyLookup {
  [propertyId: string]: { PIC?: string; name?: string | null } | undefined;
}

export function getBusinessPics(
  business: Business,
  propertyByIdLookup: PropertyLookup,
): string[] {
  return business.properties
    .map(property => propertyByIdLookup[property.id]?.PIC)
    .filter((pic): pic is string => Boolean(pic))
    .map(normalizePicOrAbn);
}

// The business's own name for each property, or the property's default name.
export function getBusinessPropertyNames(
  business: Business,
  propertyByIdLookup: PropertyLookup,
): string[] {
  return business.properties
    .map(property => property.name || propertyByIdLookup[property.id]?.name)
    .filter((name): name is string => Boolean(name?.trim()))
    .map(normalizeLower);
}

// Identifiers match exactly; only name is fuzzy.
const NAME_FUSE_OPTIONS: Fuse.IFuseOptions<{ name: string }> = {
  keys: [{ name: "name", weight: 1 }],
  includeScore: true,
  isCaseSensitive: false,
  minMatchCharLength: 2,
  shouldSort: true,
  ignoreLocation: true,
  ignoreFieldNorm: true,
  threshold: 0.3,
};

// Existing Businesses matching a temporary Business; identifier matches rank
// ahead of name matches. The pool is built once per matcher.
export function buildBusinessMatcher(
  candidates: Business[],
  propertyByIdLookup: PropertyLookup,
): (temporaryBusiness: Business) => BusinessMatchCandidate[] {
  // Same pool the search offers (active, not temporary), so a suggestion can
  // always be found there too. Businesses added from Connected Businesses
  // (isGlobal) are the user's own, so they're included.
  const pool = candidates
    .filter(candidate => candidate.isActive && !candidate.isTemporary)
    .map(business => ({
      business,
      pics: getBusinessPics(business, propertyByIdLookup),
      propertyNames: getBusinessPropertyNames(business, propertyByIdLookup),
    }));
  const fuse = new Fuse<Business>(
    pool.map(({ business }) => business),
    NAME_FUSE_OPTIONS,
  );
  const cache = new WeakMap<Business, BusinessMatchCandidate[]>();

  return temporaryBusiness => {
    const cached = cache.get(temporaryBusiness);
    if (cached) {
      return cached;
    }

    const temporaryPics = getBusinessPics(
      temporaryBusiness,
      propertyByIdLookup,
    );
    const temporaryPropertyNames = getBusinessPropertyNames(
      temporaryBusiness,
      propertyByIdLookup,
    );

    const identifierMatches: BusinessMatchCandidate[] = [];
    const excludedIds = new Set<string>([temporaryBusiness.id]);

    pool.forEach(candidate => {
      if (candidate.business.id === temporaryBusiness.id) {
        return;
      }

      const matchedFields: BusinessMatchedField[] = [];

      if (candidate.pics.some(pic => temporaryPics.includes(pic))) {
        matchedFields.push("pic");
      }

      if (
        candidate.propertyNames.some(name =>
          temporaryPropertyNames.includes(name),
        )
      ) {
        matchedFields.push("propertyName");
      }

      if (matchedFields.length) {
        excludedIds.add(candidate.business.id);
        identifierMatches.push({ business: candidate.business, matchedFields });
      }
    });
    const trimmedName = temporaryBusiness.name?.trim();
    const nameResults = trimmedName ? fuse.search(trimmedName) : [];
    const nameMatchIds = new Set(nameResults.map(result => result.item.id));
    identifierMatches.forEach(match => {
      if (nameMatchIds.has(match.business.id)) {
        match.matchedFields.unshift("name");
      }
    });
    identifierMatches.sort(
      (a, b) => b.matchedFields.length - a.matchedFields.length,
    );

    const nameMatches: BusinessMatchCandidate[] = nameResults
      .filter(result => !excludedIds.has(result.item.id))
      .map(result => ({ business: result.item, matchedFields: ["name"] }));

    const matches = [...identifierMatches, ...nameMatches];
    cache.set(temporaryBusiness, matches);
    return matches;
  };
}

export function findBusinessMatchCandidates(
  candidates: Business[],
  temporaryBusiness: Business,
  propertyByIdLookup: PropertyLookup,
): BusinessMatchCandidate[] {
  return buildBusinessMatcher(
    candidates,
    propertyByIdLookup,
  )(temporaryBusiness);
}

// Shared by every TemporaryBusiness card (and the list grouping), so the pool
// is only rebuilt when businesses or properties actually change.
export const selectBusinessMatcher = createSelector(
  [getBusinessesById, getProperties],
  (businesses, propertyByIdLookup) =>
    buildBusinessMatcher(Object.values(businesses), propertyByIdLookup),
);

export function getSideLoadedPics(record: SideLoadedBusiness): string[] {
  return record.pics
    ? record.pics.split(PIC_DELIMITER).map(normalizePicOrAbn).filter(Boolean)
    : [];
}

function isUnusedSideLoadedRecord(record: SideLoadedBusiness): boolean {
  return !record.isIgnored && !record.businessIds.length;
}

export interface SideLoadedMatchCandidate {
  record: SideLoadedBusiness;
  pics: string[];
  matchedFields: BusinessMatchedField[];
}

// Side-loaded (legacy) records matching a temporary Business, excluding
// linked and ignored ones.
export function buildSideLoadedMatcher(
  records: SideLoadedBusiness[],
  propertyByIdLookup: PropertyLookup,
): (temporaryBusiness: Business) => SideLoadedMatchCandidate[] {
  const pool = records.filter(isUnusedSideLoadedRecord).map(record => ({
    record,
    pics: getSideLoadedPics(record),
  }));
  const poolByRecordId = new Map(
    pool.map(candidate => [candidate.record.id, candidate]),
  );
  const fuse = new Fuse<SideLoadedBusiness>(
    pool.map(({ record }) => record),
    NAME_FUSE_OPTIONS,
  );
  const cache = new WeakMap<Business, SideLoadedMatchCandidate[]>();

  return temporaryBusiness => {
    const cached = cache.get(temporaryBusiness);
    if (cached) {
      return cached;
    }

    const temporaryPics = getBusinessPics(
      temporaryBusiness,
      propertyByIdLookup,
    );

    // Legacy records have no property names, so only PICs are compared.
    const identifierMatches: SideLoadedMatchCandidate[] = [];
    const matchedIds = new Set<string>();

    pool.forEach(candidate => {
      const matchedFields: BusinessMatchedField[] = [];
      if (candidate.pics.some(pic => temporaryPics.includes(pic))) {
        matchedFields.push("pic");
      }
      if (matchedFields.length) {
        matchedIds.add(candidate.record.id);
        identifierMatches.push({
          record: candidate.record,
          pics: candidate.pics,
          matchedFields,
        });
      }
    });
    const trimmedName = temporaryBusiness.name?.trim();
    const nameResults = trimmedName ? fuse.search(trimmedName) : [];
    const nameMatchIds = new Set(nameResults.map(result => result.item.id));
    identifierMatches.forEach(match => {
      if (nameMatchIds.has(match.record.id)) {
        match.matchedFields.unshift("name");
      }
    });
    identifierMatches.sort(
      (a, b) => b.matchedFields.length - a.matchedFields.length,
    );

    const nameMatches: SideLoadedMatchCandidate[] = nameResults
      .filter(result => !matchedIds.has(result.item.id))
      .map(result => ({
        record: result.item,
        pics: poolByRecordId.get(result.item.id)?.pics ?? [],
        matchedFields: ["name"],
      }));

    const matches = [...identifierMatches, ...nameMatches];
    cache.set(temporaryBusiness, matches);
    return matches;
  };
}

const getSideLoadedBusinessesById = (state: State) =>
  getSideLoadedBusinesses(state) as unknown as Record<
    string,
    SideLoadedBusiness
  >;

export const selectSideLoadedMatcher = createSelector(
  [getSideLoadedBusinessesById, getProperties],
  (records, propertyByIdLookup) =>
    buildSideLoadedMatcher(Object.values(records), propertyByIdLookup),
);

// A match candidate for a temporary Business, from any source.
export type MatchSource = "business" | "connected" | "sideLoaded";

export interface MatchCandidate {
  source: MatchSource;
  id: string;
  name: string;
  pics: string[];
  abn: string | null;
  email: string | null;
  matchedFields: BusinessMatchedField[];
}

export function businessToMatchCandidate(
  business: Business,
  matchedFields: BusinessMatchedField[],
  propertyByIdLookup: PropertyLookup,
  registrationsById: Record<string, BusinessRegistration>,
): MatchCandidate {
  return {
    source: "business",
    id: business.id,
    name: business.name,
    pics: getBusinessPics(business, propertyByIdLookup),
    abn:
      (business.registrationId &&
        registrationsById[business.registrationId]?.abn) ||
      null,
    email: business.primaryContactEmail || null,
    matchedFields,
  };
}

export const getIsResolvingTemporaryBusiness =
  (businessId: string) =>
  (state: State): boolean =>
    Boolean(state.businessesV2.temporaryBusinessResolves?.[businessId]);

// A result from the Connected (global) Business search.
export interface ConnectedBusinessSearchResult {
  id: string;
  name: string;
  PICs?: string[] | string | null;
  ABN?: string | null;
  matched_fields?: string[];
}

const CONNECTED_MATCHED_FIELDS: Record<string, BusinessMatchedField> = {
  pics: "pic",
  name: "name",
  trading_name: "name",
};

export function connectedToMatchCandidate(
  result: ConnectedBusinessSearchResult,
): MatchCandidate {
  const pics = Array.isArray(result.PICs)
    ? result.PICs
    : (result.PICs || "").split(/[,\s]+/);
  return {
    source: "connected",
    id: result.id,
    name: result.name,
    pics: pics.map(normalizePicOrAbn).filter(Boolean),
    abn: result.ABN || null,
    email: null,
    matchedFields: Array.from(
      new Set(
        (result.matched_fields || [])
          .map(field => CONNECTED_MATCHED_FIELDS[field])
          .filter(Boolean),
      ),
    ),
  };
}

export function sideLoadedToMatchCandidate(
  record: SideLoadedBusiness,
  matchedFields: BusinessMatchedField[],
): MatchCandidate {
  return {
    source: "sideLoaded",
    id: record.id,
    name: record.name,
    pics: getSideLoadedPics(record),
    abn: record.abn || null,
    email: record.email || null,
    matchedFields,
  };
}

function byMostRecentlyCreated(a: Business, b: Business): number {
  return new Date(b.created).getTime() - new Date(a.created).getTime();
}

const selectSaleId = (_state: State, saleId: number) => saleId;

// Temporary Businesses created in a Sale (by addedInLivestockSaleId, not lot
// roles).
export const selectTemporaryBusinessesForSale = createSelector(
  [getBusinessesById, selectSaleId],
  (businesses, saleId): Business[] =>
    Object.values(businesses)
      .filter(
        business =>
          business.isTemporary && business.addedInLivestockSaleId === saleId,
      )
      .sort(byMostRecentlyCreated),
);

export const selectAllTemporaryBusinesses = createSelector(
  [getBusinessesById],
  (businesses): Business[] =>
    Object.values(businesses)
      .filter(business => business.isTemporary)
      .sort(byMostRecentlyCreated),
);

// The current Sale's temporary Businesses that are actually used in it (on a
// lot, consignment, split, sundry, bid, pen, nomination, etc.) - ones created
// but never used don't need resolving there.
export const selectUsedTemporaryBusinessesInCurrentSale = createSelector(
  [
    (state: State) =>
      selectTemporaryBusinessesForSale(state, getLivestockSaleId()),
    (state: State) =>
      selectBusinessesIdsInSaleByType(state) as unknown as Record<
        string,
        string[]
      >,
  ],
  (temporaryBusinesses, businessIdsByType): Business[] => {
    const businessIdsInSale = new Set(Object.values(businessIdsByType).flat());
    return temporaryBusinesses.filter(business =>
      businessIdsInSale.has(business.id),
    );
  },
);

export const selectUnusedSideLoadedCandidates = createSelector(
  [getSideLoadedBusinessesById],
  (records): MatchCandidate[] =>
    Object.values(records)
      .filter(isUnusedSideLoadedRecord)
      .map(record => sideLoadedToMatchCandidate(record, [])),
);
